import type { HttpErrorContext } from './observability.types';
import { readRequestDuration } from './timing';

const REDACTED = '[REDACTED]';

/**
 * Chaves que nunca saem da aplicação, em qualquer profundidade.
 *
 * ATENÇÃO: `sendTokenizeCard` faz POST com `card.number` e `cvv`, e o
 * `AxiosError` carrega esse corpo em `config.data`. Sem esta lista e sem a
 * allowlist de `extractHttpContext`, um PAN em texto claro chega no Datadog.
 * Campo novo aqui precisa ser avaliado contra esse corpo.
 */
const SENSITIVE_KEYS = new Set([
  'number',
  'cardnumber',
  'creditcard',
  'card',
  'cvv',
  'cvc',
  'securitycode',
  'holdername',
  'cardholder',
  'expmonth',
  'expyear',
  'expirationdate',
  'document',
  'cpf',
  'cnpj',
  'email',
  'phone',
  'mobilephone',
  'password',
  'token',
  'cardtoken',
  'tdstoken',
  'accesstoken',
  'publickey',
  'appid',
  'authorization',
  'grecaptcharesponse',
  'data',
  'headers',
  'config',
  'request',
]);

const PATTERNS: Array<[RegExp, string]> = [
  // PAN, 13-19 dígitos. Amplo de propósito: sobre-redigir é aceitável,
  // sub-redigir não. Separador só entre dígitos, senão engole o espaço seguinte.
  [/\b\d(?:[ -]?\d){12,18}\b/g, '[CARD]'],
  // CNPJ formatado e cru
  [/\b\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}\b/g, '[DOC]'],
  [/\b\d{14}\b/g, '[DOC]'],
  // CPF formatado e cru
  [/\b\d{3}\.\d{3}\.\d{3}-\d{2}\b/g, '[DOC]'],
  [/\b\d{11}\b/g, '[DOC]'],
  [/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[EMAIL]'],
];

const isSensitiveKey = (key: string) =>
  SENSITIVE_KEYS.has(key.toLowerCase().replace(/[-_\s]/g, ''));

/** Aplica os padrões de PII sobre string livre. */
export function redactString(value: string): string {
  return PATTERNS.reduce(
    (acc, [pattern, replacement]) => acc.replace(pattern, replacement),
    value
  );
}

const MAX_DEPTH = 4;
const MAX_ARRAY_ITEMS = 10;

/** Rede de segurança para o contexto que o caller passa. */
export function scrubContext(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return redactString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (depth >= MAX_DEPTH) return REDACTED;

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => scrubContext(item, depth + 1));
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((acc, [key, item]) => {
      acc[key] = isSensitiveKey(key) ? REDACTED : scrubContext(item, depth + 1);
      return acc;
    }, {});
  }

  return REDACTED;
}

const hasKey = <K extends string>(
  value: unknown,
  key: K
): value is Record<K, unknown> =>
  typeof value === 'object' && value !== null && key in value;

/** Só o path: a query carrega `appId`. */
export function sanitizePath(url?: string): string | undefined {
  if (!url) return undefined;
  const withoutQuery = url.split(/[?#]/)[0];
  return redactString(withoutQuery.replace(/^https?:\/\/[^/]+/, ''));
}

function collectApiErrors(data: unknown) {
  const codes: string[] = [];
  const messages: string[] = [];

  const push = (entry: unknown) => {
    if (typeof entry === 'string') {
      messages.push(redactString(entry));
      return;
    }
    if (hasKey(entry, 'code') && typeof entry.code === 'string') {
      codes.push(entry.code);
    }
    if (hasKey(entry, 'message') && typeof entry.message === 'string') {
      messages.push(redactString(entry.message));
    }
  };

  if (hasKey(data, 'message') && typeof data.message === 'string') {
    messages.push(redactString(data.message));
  }

  const errors = hasKey(data, 'errors') ? data.errors : undefined;

  if (Array.isArray(errors)) {
    errors.slice(0, MAX_ARRAY_ITEMS).forEach(push);
  } else if (typeof errors === 'object' && errors !== null) {
    // `{ errors: { campo: ['msg'] } }` — o nome do campo não é PII, o valor pode ser.
    Object.entries(errors)
      .slice(0, MAX_ARRAY_ITEMS)
      .forEach(([field, value]) => {
        codes.push(field);
        (Array.isArray(value) ? value : [value]).forEach(push);
      });
  }

  return { codes, messages };
}

/**
 * Extrai contexto de erro HTTP por **allowlist**. Nunca lê `config.data`,
 * `config.headers`, `request` nem `response.data` inteiro.
 */
export function extractHttpContext(error: unknown): HttpErrorContext {
  if (typeof error !== 'object' || error === null) return {};

  const context: HttpErrorContext = {};

  if (hasKey(error, 'code') && typeof error.code === 'string') {
    context.http_error_code = error.code;
  }

  const config = hasKey(error, 'config') ? error.config : undefined;
  if (hasKey(config, 'method') && typeof config.method === 'string') {
    context.http_method = config.method.toUpperCase();
  }
  if (hasKey(config, 'url') && typeof config.url === 'string') {
    context.http_path = sanitizePath(config.url);
  }

  const duration = readRequestDuration(config);
  if (duration !== undefined) context.http_duration_ms = duration;

  const response = hasKey(error, 'response') ? error.response : undefined;
  if (!response) return context;

  if (hasKey(response, 'status') && typeof response.status === 'number') {
    context.http_status = response.status;
  }

  const headers = hasKey(response, 'headers') ? response.headers : undefined;
  if (typeof headers === 'object' && headers !== null) {
    const bag = headers as Record<string, unknown>;
    // Hoje quase sempre `undefined`: o BFF responde
    // `access-control-expose-headers: Location`, então o browser não entrega
    // header de correlação ao JS. Passa a funcionar quando expuserem.
    const requestId =
      bag['x-kong-request-id'] ??
      bag['x-request-id'] ??
      bag['x-correlation-id'];
    if (typeof requestId === 'string') context.request_id = requestId;
  }

  if (hasKey(response, 'data')) {
    const { codes, messages } = collectApiErrors(response.data);
    if (codes.length) context.api_error_codes = codes;
    if (messages.length) context.api_error_messages = messages;
  }

  return context;
}
